/**
 * TanStack Query Client Configuration
 * 
 * Centralized QueryClient setup with default options for data fetching.
 * This client should be provided to the application via QueryClientProvider in App.tsx.
 * 
 * @example
 * ```tsx
 * import { QueryClientProvider } from '@tanstack/react-query'
 * import { queryClient } from './modules/shared'
 * 
 * function App() {
 *   return (
 *     <QueryClientProvider client={queryClient}>
 *       <YourApp />
 *     </QueryClientProvider>
 *   )
 * }
 * ```
 */
import { QueryClient } from '@tanstack/react-query'

/**
 * Tiered stale times — import in individual hooks to opt into the right freshness.
 *
 * social  — feed, reactions, notifications: high change rate, reconcile quickly
 * profile — user profiles, agent data: moderate change rate
 * stable  — block lists, settings, reference data: rarely changes
 */
export const STALE_TIMES = {
  social: 30 * 1000,
  profile: 2 * 60 * 1000,
  stable: 5 * 60 * 1000,
} as const

/** Global default — conservative; social queries should override with STALE_TIMES.social */
const DEFAULT_STALE_TIME = STALE_TIMES.stable

/**
 * Default garbage collection time for queries (10 minutes)
 * Inactive queries are removed from cache after this time
 */
const DEFAULT_GC_TIME = 10 * 60 * 1000

/**
 * Retry configuration
 */
const QUERY_RETRY_COUNT = 3
const MUTATION_RETRY_COUNT = 0

/**
 * Global QueryClient instance
 * 
 * Configuration:
 * - staleTime: 5 minutes - Data stays fresh, reduces unnecessary requests
 * - gcTime: 10 minutes - Cache retention for inactive queries
 * - retry: 3 for queries (network resilience), 1 for mutations (avoid duplicates)
 * - refetchOnWindowFocus: true - Refresh when user returns to app
 * - refetchOnReconnect: true - Refresh when network recovers
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: DEFAULT_STALE_TIME,
      gcTime: DEFAULT_GC_TIME,
      retry: (failureCount, error) => {
        // Never retry on 429 (rate limit) or 4xx client errors
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status
          if (status === 429 || (status >= 400 && status < 500)) return false
        }
        return failureCount < QUERY_RETRY_COUNT
      },
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: MUTATION_RETRY_COUNT,
    },
  },
})

/**
 * Helper to invalidate multiple query keys at once
 */
export function invalidateQueries(keys: string[][]): Promise<void> {
  return Promise.all(
    keys.map(key => queryClient.invalidateQueries({ queryKey: key }))
  ).then(() => undefined)
}

/**
 * Helper to prefetch a query
 */
export function prefetchQuery<T>(
  queryKey: unknown[],
  queryFn: () => Promise<T>,
  staleTime = DEFAULT_STALE_TIME
): Promise<void> {
  return queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime,
  })
}
